import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchUsersOnProjectStats,
  selectUsersOnProjectStats,
  selectUsersOnProjectStatsLoading,
  selectUsersOnProjectStatsError,
} from '../../redux/account';
import { UserOnProjectStatsDto } from '../../redux/accounts';
import { DateTime } from 'luxon';
import {
  AccountDetail,
  ProjectDetail,
  ProjectedOperationalCostLog,
  ProjectedRevenuePayCostLog,
} from '../../types';
import { priceFormat } from '../../components/tableCellRenders';
import * as _ from 'lodash';
import {
  getProjectedRevenueLogsAmount,
  getTypePrompt,
} from '../../utils/business';
import Papaparse from 'papaparse';
import { downloadCsv } from '../reports/Reports.utils';

interface Props {
  account: AccountDetail;
  open: boolean;
  onClose: () => void;
}

export const FinanceStats: React.FC<Props> = ({ account, open, onClose }) => {
  const dispatch = useAppDispatch();
  const userOnProjectStats = useAppSelector(selectUsersOnProjectStats);
  const loading = useAppSelector(selectUsersOnProjectStatsLoading);
  const error = useAppSelector(selectUsersOnProjectStatsError);

  useEffect(() => {
    if (open && account.id) {
      dispatch(fetchUsersOnProjectStats(account.id));
    }
  }, [dispatch, account.id, open]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Account Finance Statistics</Typography>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        <Paper elevation={1}>
          {/* Account Finance Stats */}
          <Typography variant="h2" marginLeft={'16px'} marginTop={'16px'}>
            Account Finance Stats
          </Typography>
          <Table
            sx={{
              backgroundColor: 'white',
              '& .MuiTableCell-root': { paddingBlock: '8px' },
              marginBottom: '16px',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Past Operational Cost</TableCell>
                <TableCell>Projected Operational Cost</TableCell>
                <TableCell>Past Pay Cost</TableCell>
                <TableCell>Projected Pay Cost</TableCell>
                <TableCell>Past Revenue</TableCell>
                <TableCell>Projected Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ paddingBlock: 0, marginBlock: 0, height: 0 }}>
              {account.projects.map((project: any) => (
                <TableRow
                  key={project.name}
                  sx={{
                    height: '0 !important',
                  }}
                >
                  <TableCell>{project.name}</TableCell>
                  <TableCell>
                    <Typography align={'right'}>
                      {priceFormat({
                        value: project.pastOperationalCost,
                        hideCents: true,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography align={'right'}>
                      {priceFormat({
                        value: project.projectedOperationalCost,
                        hideCents: true,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography align={'right'}>
                      {priceFormat({
                        value: project.pastPayCost,
                        hideCents: true,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography align={'right'}>
                      {priceFormat({
                        value: project.projectedPayCost,
                        hideCents: true,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography align={'right'}>
                      {priceFormat({
                        value: project.pastRevenue,
                        hideCents: true,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography align={'right'}>
                      {priceFormat({
                        value: project.projectedRevenue,
                        hideCents: true,
                      })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow
                sx={{
                  height: '0 !important',
                }}
              >
                <TableCell>Total</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Typography align={'right'}>
                    {priceFormat({
                      value: account.pastOperationalCost,
                      hideCents: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography align={'right'}>
                    {priceFormat({
                      value: account.projectedOperationalCost,
                      hideCents: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography align={'right'}>
                    {priceFormat({
                      value: account.pastPayCost,
                      hideCents: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography align={'right'}>
                    {priceFormat({
                      value: account.projectedPayCost,
                      hideCents: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography align={'right'}>
                    {priceFormat({
                      value: account.pastRevenue,
                      hideCents: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography align={'right'}>
                    {priceFormat({
                      value: account.projectedRevenue,
                      hideCents: true,
                    })}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography>
                    Gross Margin = (${account.projectedRevenue} + $
                    {account.pastRevenue} - ${account.projectedPayCost} - $
                    {account.pastPayCost}) / (${account.projectedRevenue} + $
                    {account.pastRevenue}) = {account.grossMargin}%
                  </Typography>
                  <Typography>
                    Net Margin = (${account.projectedRevenue} + $
                    {account.pastRevenue} - ${account.projectedPayCost} - $
                    {account.pastPayCost} - ${account.projectedOperationalCost}{' '}
                    - ${account.pastOperationalCost}) / ($
                    {account.projectedRevenue} + ${account.pastRevenue}) ={' '}
                    {account.netMargin}%
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {/* Project Finance Stats */}
          {account.projects.map((project: any) => (
            <ProjectFinanceStats key={project.name} project={project} />
          ))}

          {!loading &&
            !error &&
            userOnProjectStats?.userOnProjectStats?.length && (
              <UsersOnProjectStatsTable
                userOnProjectStats={userOnProjectStats?.userOnProjectStats}
              />
            )}

          {!loading &&
            !error &&
            (!userOnProjectStats ||
              userOnProjectStats.userOnProjectStats.length === 0) && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <Typography variant="body1" color="textSecondary">
                  No users found for this project.
                </Typography>
              </Box>
            )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const USERS_ON_PROJECT_REPORT_KEYS = {
  UserFullName: 'User Full Name',
  ProjectName: 'Project Name',
  FromDate: 'From Date',
  ToDate: 'To Date',
  Days: 'Days',
  HourlyPayRate: 'Hourly Pay Rate',
  OperationalCost: 'Operational Cost',
  Allocation: 'Allocation',
  OperationalCostPerPeriod: 'Operational Cost Per Period',
  PayCostPerPeriod: 'Pay Cost Per Period',
};

const UsersOnProjectStatsTable = ({
  userOnProjectStats,
}: {
  userOnProjectStats: UserOnProjectStatsDto[];
}) => {
  const rows = useMemo(() => {
    return userOnProjectStats.flatMap((userOnProjectStatsElem) => {
      return userOnProjectStatsElem.logs.flatMap((log: any) => {
        return {
          [USERS_ON_PROJECT_REPORT_KEYS.UserFullName]:
            userOnProjectStatsElem.userFullName,
          [USERS_ON_PROJECT_REPORT_KEYS.ProjectName]:
            userOnProjectStatsElem.projectName,
          [USERS_ON_PROJECT_REPORT_KEYS.FromDate]: DateTime.fromISO(
            log.fromDate,
          ).toLocaleString(DateTime.DATE_MED),
          [USERS_ON_PROJECT_REPORT_KEYS.ToDate]: DateTime.fromISO(
            log.toDate,
          ).toLocaleString(DateTime.DATE_MED),
          [USERS_ON_PROJECT_REPORT_KEYS.Days]: log.days,
          [USERS_ON_PROJECT_REPORT_KEYS.HourlyPayRate]: log.hourlyPayRate,
          [USERS_ON_PROJECT_REPORT_KEYS.OperationalCost]: log.operationalCost,
          [USERS_ON_PROJECT_REPORT_KEYS.Allocation]: log.allocation,
          [USERS_ON_PROJECT_REPORT_KEYS.OperationalCostPerPeriod]: _.round(
            log.operationalCost * log.days * 8 * (log.allocation / 100),
            2,
          ),
          [USERS_ON_PROJECT_REPORT_KEYS.PayCostPerPeriod]: _.round(
            log.hourlyPayRate * log.days * 8 * (log.allocation / 100),
            2,
          ),
        };
      });
    });
  }, [userOnProjectStats]);

  const { totalOperationalCost, totalPayCost } = useMemo(() => {
    return {
      totalOperationalCost: _.round(
        rows.reduce(
          (sum, log) =>
            sum +
            log['Operational Cost'] * log.Days * 8 * (log.Allocation / 100),
          0,
        ),
        2,
      ),
      totalPayCost: _.round(
        rows.reduce(
          (sum, log) =>
            sum +
            log['Hourly Pay Rate'] * log.Days * 8 * (log.Allocation / 100),
          0,
        ),
        2,
      ),
    };
  }, [rows]);

  const handleDownloadCSV = useCallback(() => {
    const csvData = Papaparse.unparse(rows, {
      quotes: true,
      columns: Object.values(USERS_ON_PROJECT_REPORT_KEYS),
    });

    const currentDate = DateTime.now().toFormat('yyyy-MM-dd');
    const csvFilename = `users-on-project-stats-${currentDate}.csv`;

    downloadCsv(csvData, csvFilename);
  }, [rows]);

  return (
    <>
      <Typography variant="h2" marginLeft={'16px'}>
        Users on Project Stats
      </Typography>
      {/* Users on Project Stats */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Project</TableCell>

            <TableCell>From Date</TableCell>
            <TableCell>To Date</TableCell>
            <TableCell>Days</TableCell>

            <TableCell>Hourly Pay Rate</TableCell>
            <TableCell>Operational Cost Rate</TableCell>
            <TableCell>Allocation</TableCell>

            <TableCell>Operational Cost Per Period</TableCell>
            <TableCell>Pay Cost Per Period</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {userOnProjectStats.map((userOnProjectStatsElem) => (
            <UsersOnProjectStatsRows
              key={`${userOnProjectStatsElem.userFullName}-${userOnProjectStatsElem.projectName}`}
              userOnProjectStatsElem={userOnProjectStatsElem}
            />
          ))}

          <TableRow>
            <TableCell
              sx={{ padding: '1px', border: '1px solid #e0e0e0' }}
              colSpan={8}
            >
              <Typography variant="body2">Total</Typography>
            </TableCell>
            <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
              <Typography variant="body2">
                {priceFormat({
                  value: totalOperationalCost,
                  hideCents: true,
                })}
              </Typography>
            </TableCell>
            <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
              <Typography variant="body2">
                {priceFormat({
                  value: totalPayCost,
                  hideCents: true,
                })}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Button onClick={handleDownloadCSV}>Download CSV</Button>
    </>
  );
};

interface UsersOnProjectStatsRowsProps {
  userOnProjectStatsElem: UserOnProjectStatsDto;
}

export const UsersOnProjectStatsRows: React.FC<
  UsersOnProjectStatsRowsProps
> = ({ userOnProjectStatsElem }) => {
  return (
    <>
      {userOnProjectStatsElem.logs.map((log) => {
        return (
          <React.Fragment key={`${log.fromDate}-${log.toDate}`}>
            <TableRow
              key={`${log.fromDate}-${log.toDate}`}
              sx={{
                height: '0 !important',
              }}
            >
              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="medium">
                  {userOnProjectStatsElem.userFullName}
                </Typography>
              </TableCell>
              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="medium">
                  {userOnProjectStatsElem.projectName}
                </Typography>
              </TableCell>

              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2">
                  {DateTime.fromISO(log.fromDate).toLocaleString(
                    DateTime.DATE_MED,
                  )}
                </Typography>
              </TableCell>
              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2">
                  {DateTime.fromISO(log.toDate).toLocaleString(
                    DateTime.DATE_MED,
                  )}
                </Typography>
              </TableCell>
              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2">{log.days}</Typography>
              </TableCell>

              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2">{log.hourlyPayRate}</Typography>
              </TableCell>

              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2">{log.operationalCost}</Typography>
              </TableCell>

              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2">{`${log.allocation}%`}</Typography>
              </TableCell>

              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="medium">
                  {_.round(
                    log.operationalCost * log.days * 8 * (log.allocation / 100),
                    2,
                  )}
                </Typography>
              </TableCell>
              <TableCell sx={{ padding: '1px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="textSecondary">
                  {_.round(
                    log.hourlyPayRate * log.days * 8 * (log.allocation / 100),
                    2,
                  )}
                </Typography>
              </TableCell>
            </TableRow>
          </React.Fragment>
        );
      })}
    </>
  );
};

export const ProjectFinanceStats = ({
  project,
}: {
  project: ProjectDetail;
}) => {
  const totalRevenue = useMemo(
    () =>
      project.projectedRevenueLogs?.reduce((acc, log) => {
        return (
          acc +
          (getProjectedRevenueLogsAmount(0, [log], 'blendedBillRate') || 0)
        );
      }, 0) || 0,
    [project.projectedRevenueLogs],
  );

  const totalPayCost = useMemo(
    () =>
      project.projectedRevenueLogs?.reduce((acc, log, index) => {
        if (index === 0) {
          // Skip first one, since we don't calculalate pay cost from invoice date to now
          return acc;
        }

        return (
          acc + (getProjectedRevenueLogsAmount(0, [log], 'blendedPayRate') || 0)
        );
      }, 0) || 0,
    [project.projectedRevenueLogs],
  );

  const totalOperationalCost = useMemo(
    () =>
      project.projectedOperationalCostLogs?.reduce(
        (acc, log) =>
          acc +
          (getProjectedRevenueLogsAmount(0, [log], 'operationalCost') || 0),
        0,
      ) || 0,
    [project.projectedOperationalCostLogs],
  );

  return (
    <>
      <Typography variant="h2" marginLeft={'16px'} marginTop={'16px'}>
        &quot;{project.name}&quot; Projected Revenue
      </Typography>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: '2px', border: '1px solid #e0e0e0' }}>
              Type
            </TableCell>
            <TableCell sx={{ padding: '2px', border: '1px solid #e0e0e0' }}>
              Period
            </TableCell>
            <TableCell sx={{ padding: '2px', border: '1px solid #e0e0e0' }}>
              Formula
            </TableCell>
            <TableCell
              sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
              align="right"
            >
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Projected Bill Rate */}
          {project.projectedRevenueLogs?.map(
            (log: ProjectedRevenuePayCostLog, index: number) => (
              <TableRow
                key={`pay-${index}`}
                sx={{
                  height: '0 !important',
                }}
              >
                <TableCell sx={{ padding: '2px', border: '1px solid #e0e0e0' }}>
                  {getTypePrompt(log, index, project.projectedRevenueLogs)}
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    Bill Rate
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                >{`${log.fromDate} - ${log.toDate}`}</TableCell>
                <TableCell sx={{ padding: '2px', border: '1px solid #e0e0e0' }}>
                  <Typography variant="body1" marginBlock={1}>
                    {` ${log.days} days * ${log.headcount} ${
                      log.headcount > 1 ? 'contractors' : 'contractor'
                    } * $${_.round(log.blendedBillRate, 2)} * 8 `}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                  align="right"
                >
                  {priceFormat({
                    value: getProjectedRevenueLogsAmount(
                      index,
                      project.projectedRevenueLogs,
                      'blendedBillRate',
                    ),
                    hideCents: true,
                  })}
                </TableCell>
              </TableRow>
            ),
          )}
          <TableRow>
            <TableCell
              colSpan={3}
              sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
            >
              Total
            </TableCell>
            <TableCell
              colSpan={1}
              sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
            >
              {priceFormat({
                value: totalRevenue,
                hideCents: true,
              })}
            </TableCell>
          </TableRow>

          {/* Projected Pay Cost */}
          <Typography variant="h2" marginLeft={'16px'} marginTop={'16px'}>
            &quot;{project.name}&quot; Projected Pay Cost
          </Typography>

          {project.projectedRevenueLogs?.map(
            (log: ProjectedRevenuePayCostLog, index: number) => {
              if (index === 0) {
                return <></>;
              }

              return (
                <TableRow
                  key={`pay-${index}`}
                  sx={{
                    height: '0 !important',
                  }}
                >
                  <TableCell
                    sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                  >
                    {getTypePrompt(log, index, project.projectedRevenueLogs)}
                    <Typography
                      variant="caption"
                      display="block"
                      color="textSecondary"
                    >
                      Pay Cost
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                  >{`${log.fromDate} - ${log.toDate}`}</TableCell>
                  <TableCell
                    sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                  >
                    <Typography variant="body1" marginBlock={1}>
                      {` ${log.days} days * ${log.headcount} ${
                        log.headcount > 1 ? 'contractors' : 'contractor'
                      } * $${_.round(log.blendedPayRate, 2)} * 8 `}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                    align="right"
                  >
                    {priceFormat({
                      value: getProjectedRevenueLogsAmount(
                        index,
                        project.projectedRevenueLogs,
                        'blendedPayRate',
                      ),
                      hideCents: true,
                    })}
                  </TableCell>
                </TableRow>
              );
            },
          )}
          <TableRow>
            <TableCell
              colSpan={3}
              sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
            >
              Total
            </TableCell>
            <TableCell
              colSpan={1}
              sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
            >
              {priceFormat({
                value: totalPayCost,
                hideCents: true,
              })}
            </TableCell>
          </TableRow>

          {/* Projected Operational Cost */}
          <Typography variant="h2" marginLeft={'16px'} marginTop={'16px'}>
            &quot;{project.name}&quot; Projected Operational Cost
          </Typography>

          {project.projectedOperationalCostLogs?.map(
            (log: ProjectedOperationalCostLog, index: number) => (
              <TableRow
                key={`op-${index}`}
                sx={{
                  height: '0 !important',
                }}
              >
                <TableCell
                  sx={{
                    padding: '2px',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    Operational Cost
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                >{`${log.fromDate} - ${log.toDate}`}</TableCell>
                <TableCell sx={{ padding: '2px', border: '1px solid #e0e0e0' }}>
                  <Typography variant="body1" marginBlock={1}>
                    {` ${log.days} days * ${log.headcount} ${
                      log.headcount > 1 ? 'contractors' : 'contractor'
                    } * $${_.round(log.operationalCost, 2)} * 8 `}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
                  align="right"
                >
                  {priceFormat({
                    value: getProjectedRevenueLogsAmount(
                      index,
                      project.projectedOperationalCostLogs,
                      'operationalCost',
                    ),
                    hideCents: true,
                  })}
                </TableCell>
              </TableRow>
            ),
          )}
          <TableRow>
            <TableCell
              colSpan={3}
              sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
            >
              Total
            </TableCell>
            <TableCell
              colSpan={1}
              sx={{ padding: '2px', border: '1px solid #e0e0e0' }}
            >
              {priceFormat({
                value: totalOperationalCost,
                hideCents: true,
              })}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default FinanceStats;
